import { trackCreatePatientProfile } from '@/lib/analytics/scribe/link';
import { trackPatientCreated } from '@/lib/analytics/scribe/patient';
import { type PatientCreatedEventProps } from '@/models/analytics/features/patient';
import { postMemberPatientProfile } from '@/pages/api/scribeAPIV2';
import { extractAxiosErrorDetail } from '@/utils/axios-error';
import { toast } from '@heidi/design-system/toast';
import { type CreatePatientProfileRequest } from '@heidi/patients/contracts';
import { useMutateCreateOwnPatientProfile as useMutateCreateOwnPatientProfileCore } from '@heidi/patients/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { PatientSource } from '@heidi/schema/ml-scribe';
import { isViewingOtherSessionAtom } from '@heidi/session/data';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import { GET_TEAM_MEMBER_PATIENT_PROFILES } from '../query/useQueryPatientProfiles';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';
import { useMutateLinkSessionsToPatientProfile } from './useMutateLinkSessionsToPatientProfile';

/**
 * Optional funnel metadata for the `patient_created` Snowplow event.
 * Populated by find-or-link callers (combobox surface + create match method
 * + search_id stitching). Legacy callers (EHR launch, drawer, integrations)
 * leave it undefined and emit the bare event.
 */
type PatientCreatedTrackingMetadata = Omit<
  PatientCreatedEventProps,
  'patient_id' | 'session_id' | 'is_from_ehr'
>;

type CreatePatientProfileProps = {
  patientProfile: CreatePatientProfileRequest;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
  /**
   * Optional analytics metadata spread into the `patient_created` event in
   * onSuccess. Carries `surface`, `match_method` (create-flavour enum),
   * `result_count_seen`, `time_to_outcome_ms`, `search_id`, and
   * `feature_flag_variant` for the find-or-link combobox funnel; legacy
   * callers omit it.
   */
  tracking?: PatientCreatedTrackingMetadata;
};

type CreateAndLinkPatientProfileProps = {
  patientProfile: CreatePatientProfileRequest;
  sessionIds: string[];
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
  /**
   * Optional analytics metadata. The composite passes this to the
   * `patient_created` event as-is, and forwards everything except
   * `match_method` to the downstream `patient_linked` event — there's no
   * "match" when create-and-link fires, so match_method is dropped on the
   * linked side.
   */
  tracking?: PatientCreatedTrackingMetadata;
};

export function useMutateCreateAndLinkPatientProfile() {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  const createAndLinkTeamMemberProfile = useMutateCreateAndLinkTeamMemberPatientProfile();
  const createAndLinkOwnProfile = useMutateCreateAndLinkOwnPatientProfile();

  return useMutation({
    mutationFn: async (input: CreateAndLinkPatientProfileProps) => {
      if (isViewingOtherSession && input.sessionId) {
        return createAndLinkOwnProfile.mutateAsync(input);
      }
      if (isReviewingOtherUser) {
        return createAndLinkTeamMemberProfile.mutateAsync(input);
      }
      return createAndLinkOwnProfile.mutateAsync(input);
    },
  });
}

/**
 * Standalone create switch (no session link). Routes to the team-member
 * create endpoint when an assistant is acting on behalf of a reviewed
 * clinician, so the patient is created under the clinician
 * (managed_by = member), not the assistant. Mirrors the own/team-member
 * switch in useQueryPatientProfiles and useMutateUpdatePatientProfile so the
 * create target matches the list the Patients page is showing.
 *
 * Normalises the team-member response to `{ createPatientProfileResponse }`
 * so callers (the create-patient drawer) read a single shape regardless of
 * which endpoint handled the create.
 */
export function useMutateCreatePatientProfile() {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  const createTeamMemberProfile = useMutateCreateTeamMemberPatientProfile();
  const createOwnProfile = useMutateCreateOwnPatientProfile();

  return useMutation({
    mutationFn: async (input: CreatePatientProfileProps) => {
      if (isViewingOtherSession && input.sessionId) {
        return createOwnProfile.mutateAsync(input);
      }
      if (isReviewingOtherUser) {
        const { createTeamMemberPatientProfileResponse } =
          await createTeamMemberProfile.mutateAsync(input);
        return {
          createPatientProfileResponse: createTeamMemberPatientProfileResponse,
        };
      }
      return createOwnProfile.mutateAsync(input);
    },
  });
}

/**
 * App-side delegate for `@heidi/patients/data` (D2.2, ARCFE-1053).
 *
 * The request, the `GET_PATIENT_PROFILES` invalidation and the direct-share
 * `session_id` decision live in the package. What stays here is the app
 * reaction set: the toast (intl copy + the `w-auto` width note), the Rematch
 * analytics dispatch, and the `patient_created` Snowplow event — plus the
 * `AxiosError` narrowing, because `extractAxiosErrorDetail` and the fallback
 * copy are both app infrastructure.
 */
export function useMutateCreateOwnPatientProfile() {
  const intl = useIntl();
  const { t } = useTranslations();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutateCreateOwnPatientProfileCore<PatientCreatedTrackingMetadata>({
    isViewingOtherSession,
    onCreated: (response, input) => {
      const { demographic_string, first_name, last_name } =
        response.createPatientProfileResponse.data.data;
      const name =
        demographic_string || [first_name, last_name].filter(Boolean).join(' ') || null;
      toast({
        tone: 'success',
        title: name
          ? intl.formatMessage(
              {
                defaultMessage: 'New patient {name} created',
                id: 'K9zQavBdqa',
              },
              { name }
            )
          : intl.formatMessage({
              defaultMessage: 'New patient created',
              id: '6Ev3GGk3sd',
            }),
        // Default toast width is 400px — shrink to content so short
        // messages don't leave empty space before the close button.
        className: 'w-auto',
      });
      const isFromEHR =
        response.createPatientProfileResponse.data.data.source === PatientSource.EHR;
      trackCreatePatientProfile({ isFromEHR });
      trackPatientCreated(
        {
          ...(input.tracking ?? {}),
          patient_id: String(response.createPatientProfileResponse.data.data.id ?? ''),
          is_from_ehr: isFromEHR,
        },
        response.createPatientProfileResponse.data.data
      );
    },
    onCreateFailed: (error) => {
      if (error instanceof AxiosError) {
        // Prefer the backend's field-level validation message (e.g. an invalid
        // phone or email) over the generic fallback so the user can see what is
        // blocking the save.
        toast({
          tone: 'destructive',
          title: extractAxiosErrorDetail(error) ?? t('common.backendError'),
        });
      }
    },
  });
}

export function useMutateCreateTeamMemberPatientProfile() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  return useMutation({
    mutationFn: async (input: CreatePatientProfileProps) => {
      if (!teamId || !selectedReviewMember) {
        throw new Error('Team ID or selected review member not found');
      }
      const createTeamMemberPatientProfileResponse = await postMemberPatientProfile(
        teamId,
        selectedReviewMember,
        input.patientProfile
      );
      return { createTeamMemberPatientProfileResponse };
    },
    onSuccess: (response, input) => {
      toast({
        tone: 'success',
        title: `New patient ${response.createTeamMemberPatientProfileResponse.data.data.demographic_string} created`,
        className: 'w-auto',
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_PATIENT_PROFILES, teamId, selectedReviewMember, true],
      });
      const isFromEHR =
        response.createTeamMemberPatientProfileResponse.data.data.source ===
        PatientSource.EHR;
      trackCreatePatientProfile({ isFromEHR });
      trackPatientCreated(
        {
          ...(input.tracking ?? {}),
          patient_id: String(
            response.createTeamMemberPatientProfileResponse.data.data.id ?? ''
          ),
          is_from_ehr: isFromEHR,
        },
        response.createTeamMemberPatientProfileResponse.data.data
      );
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        // Prefer the backend's field-level validation message (e.g. an invalid
        // phone or email) over the generic fallback so the user can see what is
        // blocking the save.
        toast({
          tone: 'destructive',
          title: extractAxiosErrorDetail(error) ?? t('common.backendError'),
        });
      }
    },
  });
}

/**
 * Build the `tracking` payload forwarded to `linkSessionsToPatientProfile`
 * inside the create-and-link composites. Mirrors the create-side payload
 * except `match_method` is dropped — there is no "match" when create+link
 * fires together, so the linked-event match_method stays null.
 */
function trackingForLinkAfterCreate(
  tracking: CreateAndLinkPatientProfileProps['tracking']
) {
  if (!tracking) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { match_method, ...linkTracking } = tracking;
  return linkTracking;
}

export function useMutateCreateAndLinkOwnPatientProfile() {
  const { mutateAsync: createPatientProfile } = useMutateCreateOwnPatientProfile();
  const { mutate: linkSessionsToPatientProfile } =
    useMutateLinkSessionsToPatientProfile();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutation({
    mutationFn: async (input: CreateAndLinkPatientProfileProps) => {
      const createPatientProfileResponse = await createPatientProfile({
        patientProfile: input.patientProfile,
        sessionId: isViewingOtherSession ? input.sessionId : undefined,
        tracking: input.tracking,
      });
      if (createPatientProfileResponse.createPatientProfileResponse.data.data.id) {
        linkSessionsToPatientProfile({
          patientProfile:
            createPatientProfileResponse.createPatientProfileResponse.data.data,
          sessionIds: input.sessionIds,
          sessionId: isViewingOtherSession ? input.sessionId : undefined,
          // The create mutation already fires a "New patient X created"
          // toast — don't stack a second "Session linked to X" toast on
          // top of it for a single user action.
          suppressToast: true,
          tracking: trackingForLinkAfterCreate(input.tracking),
        });
      }
      return { createPatientProfileResponse };
    },
  });
}

export function useMutateCreateAndLinkTeamMemberPatientProfile() {
  const { mutateAsync: createTeamMemberPatientProfile } =
    useMutateCreateTeamMemberPatientProfile();
  const { mutate: linkSessionsToPatientProfile } =
    useMutateLinkSessionsToPatientProfile();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutation({
    mutationFn: async (input: CreateAndLinkPatientProfileProps) => {
      const createTeamMemberPatientProfileResponse = await createTeamMemberPatientProfile(
        {
          patientProfile: input.patientProfile,
          tracking: input.tracking,
        }
      );
      if (
        createTeamMemberPatientProfileResponse.createTeamMemberPatientProfileResponse.data
          .data.id
      ) {
        linkSessionsToPatientProfile({
          patientProfile:
            createTeamMemberPatientProfileResponse.createTeamMemberPatientProfileResponse
              .data.data,
          sessionIds: input.sessionIds,
          sessionId: isViewingOtherSession ? input.sessionId : undefined,
          suppressToast: true,
          tracking: trackingForLinkAfterCreate(input.tracking),
        });
      }
      return { createTeamMemberPatientProfileResponse };
    },
  });
}
